import { MoveRight } from 'lucide-react'
import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { employerOnboardingBanking } from 'rails_routes'
import { PayrollPayScheduleSource } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { BankClosureStrategyInput } from './BankClosureStrategyInput'
import { PayFrequencyField, PayScheduleNameField } from './FormFields'
import { PayDateFields } from './PayDateFields'
import { PayDatePreview } from './PayDatePreview'
import { type BankClosureStrategy, type PayFrequency, PayFrequencyLabels, type PayScheduleFormInputs } from '../../types/payroll'
import { useCreatePaySchedule } from '../hooks/useCreatePaySchedule'
import { useGetPaySchedules } from '../hooks/useGetPaySchedules'
import { useGetSamplePayPeriods } from '../hooks/useGetSamplePayPeriods'
import { useUpdatePaySchedule } from '../hooks/useUpdatePaySchedule'
import { Button } from '../ui/button'

const ManualDeductionsOnboardingImpl = () => {
  const { assign } = useWindowLocation()

  const { data: paySchedulesData, isLoading: isLoadingSchedules } = useGetPaySchedules()
  const createPayScheduleMutation = useCreatePaySchedule()
  const updatePayScheduleMutation = useUpdatePaySchedule()
  const currentYear = new Date().getFullYear()

  // Find an existing non-default schedule to edit (exclude the default monthly schedule)
  const existingSchedule = useMemo(() => {
    if (!paySchedulesData?.data) return null
    return paySchedulesData.data.find(
      (schedule) => schedule.source !== PayrollPayScheduleSource.default && !schedule.archived
    ) ?? null
  }, [paySchedulesData])

  const {
    control,
    getValues,
    formState: { isSubmitting, errors, isValid },
    handleSubmit,
    register,
    setValue,
    watch,
    trigger,
    reset
  } = useForm<PayScheduleFormInputs>({
    mode: 'all',
    defaultValues: {
      frequency: '' as PayFrequency,
      name: '',
      bankClosureStrategy: '' as BankClosureStrategy,
      firstDay: undefined,
      secondDay: undefined,
      examplePayDate: undefined
    }
  })

  // Preload existing schedule data into the form
  useEffect(() => {
    if (existingSchedule) {
      reset({
        frequency: existingSchedule.frequency,
        name: existingSchedule.name,
        bankClosureStrategy: existingSchedule.bank_closure_strategy,
        firstDay: existingSchedule.first_day,
        secondDay: existingSchedule.second_day,
        examplePayDate: existingSchedule.reference_pay_date
      })
    }
  }, [existingSchedule, reset])

  const onSubmit = (data: PayScheduleFormInputs) => {
    if (!isValid) return

    const scheduleData = {
      frequency: data.frequency,
      name: data.name,
      bank_closure_strategy: data.bankClosureStrategy,
      first_day: data.firstDay,
      second_day: data.secondDay,
      reference_pay_date: data.examplePayDate,
    }

    // The create API returns { redirect_to: '/employer_onboarding/banking' }, but the update API
    // returns the pay schedule entity. To avoid unnecessary API changes for this legacy flow,
    // we hardcode the redirect to banking (which matches the existing create behavior).
    const onSuccess = () => {
      window.segment?.track('employeronboarding_configure_deductions', {
        version: 'legacy',
        action: existingSchedule ? 'update' : 'create',
      })
      assign(employerOnboardingBanking.new())
    }

    if (existingSchedule) {
      updatePayScheduleMutation.mutate({
        id: existingSchedule.id,
        ...scheduleData,
      }, { onSuccess })
    } else {
      createPayScheduleMutation.mutate(scheduleData, { onSuccess })
    }
  }

  const setName = (name: PayFrequency, onChange: React.Dispatch<React.SetStateAction<string>>) => {
    setValue('name', `${PayFrequencyLabels[name]} (${currentYear})`)
    onChange(name)
  }

  const frequency = watch('frequency')

  const { data, isLoading, error } = useGetSamplePayPeriods({
    frequency,
    bank_closure_strategy: watch('bankClosureStrategy'),
    first_day: watch('firstDay'),
    second_day: watch('secondDay'),
    reference_pay_date: watch('examplePayDate'),
    isValid,
  })

  if (isLoadingSchedules) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-thatch-purple-secondary' />
      </div>
    )
  }

  return (
    <div>
      <h1 className='h1 mb-4'>We need a bit more information about your payroll setup</h1>
      <div className='label-default font-medium text-ds-text-secondary mb-8'>
        <p>
          Thatch will create payroll deductions for easy upload to your system. We'll use the provided information to ensure your employees' deductions are correctly distributed across pay periods.
        </p>
        <br />
        <p>
          You will be able to edit and create additional schedules in your Settings dashboard page after onboarding.
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-6 max-w-lg mx-auto'>
        <PayFrequencyField
          control={control}
          error={errors.frequency}
          fieldId='frequency'
          customOnChange={setName}
        />

        <PayScheduleNameField
          control={control}
          error={errors.name}
          fieldId='name'
        />

        <PayDateFields
          frequency={frequency}
          control={control}
          register={register}
          errors={errors}
          watch={watch}
          trigger={trigger}
          getValues={getValues}
        />

        <BankClosureStrategyInput
          register={register}
          error={errors.bankClosureStrategy}
        />

        {isValid && (
          <PayDatePreview isLoading={isLoading} data={data} error={error?.message} />
        )}

        <Button
          type='submit'
          disabled={isSubmitting || !isValid}
          className='w-full mt-8'
        >
          {isSubmitting ? 'Submitting...' : <span>Continue<MoveRight className='inline ml-2' size={16} /></span>}
        </Button>
      </form>
    </div>
  )
}

const ManualDeductionsOnboarding = () => {
  return <ManualDeductionsOnboardingImpl />
}

export default ManualDeductionsOnboarding
